import React, { useState, useEffect } from 'react';
import { projectFirestore, authUser } from '../../firebase/config';
import { ALL_PERMISSIONS } from '../../contexts/AuthContext';

const UserManager = () => {
    const [users, setUsers] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [newPass, setNewPass] = useState('');
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        const unsub = projectFirestore.collection('adminUsers')
            .onSnapshot((snap) => {
                const list = [];
                snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                setUsers(list);
            });
        return () => unsub();
    }, []);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newEmail || !newPass) { alert('أدخل البريد وكلمة المرور'); return; }
        if (newPass.length < 6) { alert('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return; }
        setCreating(true);
        try {
            // إنشاء المستخدم في Firebase Auth عبر دالة مساعدة
            // نحتاج لإنشاء المستخدم بدون تسجيل خروج المدير الحالي
            const credential = await authUser.createUserWithEmailAndPassword(newEmail, newPass);
            const uid = credential.user.uid;

            // حفظ بيانات المستخدم في Firestore
            await projectFirestore.collection('adminUsers').doc(uid).set({
                email: newEmail,
                displayName: newName || newEmail.split('@')[0],
                role: 'user',
                permissions: {
                    upload: false,
                    delete: false,
                    editUI: false,
                    manageUsers: false,
                    changePassword: true,
                    changeSource: false,
                    manageAccounts: false,
                },
                createdAt: new Date().toISOString(),
            });

            // إعادة تسجيل دخول المدير الحالي
            // Firebase Auth يغير المستخدم الحالي عند إنشاء حساب جديد
            // لذلك نحتاج لتنبيه المدير
            alert('تم إنشاء المستخدم بنجاح: ' + newEmail + '\nسيتم تسجيل خروجك - أعد تسجيل الدخول');
            setNewEmail('');
            setNewPass('');
            setNewName('');
        } catch (err) {
            alert('خطأ: ' + err.message);
        }
        setCreating(false);
    };

    const handleTogglePermission = async (userId, permKey, currentVal) => {
        await projectFirestore.collection('adminUsers').doc(userId).update({
            ['permissions.' + permKey]: !currentVal,
        });
    };

    const handleToggleAdmin = async (userId, currentRole) => {
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        const msg = newRole === 'admin'
            ? 'هل تريد منح هذا المستخدم صلاحية المدير الكاملة؟'
            : 'هل تريد إزالة صلاحية المدير من هذا المستخدم؟';
        if (!window.confirm(msg)) return;
        const updates = { role: newRole };
        if (newRole === 'admin') {
            updates.permissions = {
                upload: true, delete: true, editUI: true,
                manageUsers: true, changePassword: true,
                changeSource: true, manageAccounts: true,
                manageNews: true, editFooter: true,
            };
        }
        await projectFirestore.collection('adminUsers').doc(userId).update(updates);
    };

    const handleDeleteUser = async (userId, email) => {
        if (!window.confirm('هل تريد حذف المستخدم ' + email + '؟\n(سيتم حذفه من قاعدة البيانات فقط)')) return;
        await projectFirestore.collection('adminUsers').doc(userId).delete();
    };

    const handleResetPassword = async (email) => {
        try {
            await authUser.sendPasswordResetEmail(email);
            alert('تم إرسال رابط إعادة تعيين كلمة المرور إلى: ' + email);
        } catch (err) {
            alert('خطأ: ' + err.message);
        }
    };

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">إدارة المستخدمين</h4>

            {/* إضافة مستخدم جديد */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">إضافة مستخدم جديد</h5>
                    <form onSubmit={handleCreateUser}>
                        <div className="row g-2 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label">الاسم</label>
                                <input type="text" className="form-control" value={newName}
                                    onChange={e => setNewName(e.target.value)} placeholder="اسم المستخدم" />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">البريد الإلكتروني</label>
                                <input type="email" className="form-control" value={newEmail}
                                    onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" required />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">كلمة المرور</label>
                                <input type="password" className="form-control" value={newPass}
                                    onChange={e => setNewPass(e.target.value)} placeholder="6 أحرف على الأقل" required />
                            </div>
                            <div className="col-md-3">
                                <button type="submit" className="btn btn-success w-100" disabled={creating}>
                                    {creating ? 'جاري الإنشاء...' : 'إضافة مستخدم'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* قائمة المستخدمين */}
            <div className="row g-3">
                {users.map(u => (
                    <div className="col-md-6" key={u.id}>
                        <div className="card shadow-sm h-100">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <h6 className="mb-0">{u.displayName || u.email}</h6>
                                        <small className="text-muted">{u.email}</small>
                                        {u.role === 'admin' && <span className="badge bg-warning text-dark ms-2">مدير</span>}
                                    </div>
                                    <div className="btn-group btn-group-sm">
                                        <button className="btn btn-outline-primary" onClick={() => handleResetPassword(u.email)}
                                            title="إعادة تعيين كلمة المرور">
                                            <i className="fas fa-key"></i>
                                        </button>
                                        <button className={`btn ${u.role === 'admin' ? 'btn-warning' : 'btn-outline-warning'}`}
                                            onClick={() => handleToggleAdmin(u.id, u.role)}
                                            title={u.role === 'admin' ? 'إزالة صلاحية المدير' : 'منح صلاحية المدير'}>
                                            <i className="fas fa-crown"></i>
                                        </button>
                                        {u.role !== 'admin' && (
                                            <button className="btn btn-outline-danger" onClick={() => handleDeleteUser(u.id, u.email)}
                                                title="حذف المستخدم">
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <hr />
                                <p className="mb-2 fw-bold" style={{ fontSize: 14 }}>الصلاحيات:</p>
                                <div className="d-flex flex-wrap gap-2">
                                    {ALL_PERMISSIONS.map(p => {
                                        const enabled = u.permissions && u.permissions[p.key];
                                        return (
                                            <button key={p.key}
                                                className={`btn btn-sm ${enabled ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => handleTogglePermission(u.id, p.key, enabled)}
                                                disabled={u.role === 'admin'}
                                                title={enabled ? 'انقر لإزالة الصلاحية' : 'انقر لمنح الصلاحية'}>
                                                {enabled ? '✓ ' : ''}{p.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserManager;
